import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PostCard from "@/components/PostCard";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Flame, Clock } from "lucide-react";
import BetaProgramCards from "@/components/BetaProgramCards";

export default function Home() {
  const location = useLocation();
  const nav = useNavigate();
  const { slug } = useParams();
  const { user } = useAuth();
  const params = new URLSearchParams(location.search);
  const search = params.get("search") || "";
  const initialSort = params.get("sort") === "trending" ? "trending" : "recent";

  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [sort, setSort] = useState(initialSort);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [postsRes, catsRes, tagsRes] = await Promise.all([
        api.get("/posts", { params: { category: slug, sort, search: search || undefined } }),
        api.get("/categories"),
        api.get("/stats/trending-tags"),
      ]);
      setPosts(postsRes.data);
      setCategories(catsRes.data);
      setTags(tagsRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load feed");
    } finally {
      setLoading(false);
    }
  }, [slug, sort, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLike = async (post) => {
    if (!user || user === false) {
      toast.error("Log in to like posts");
      nav("/login");
      return;
    }
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setPosts((ps) => ps.map((p) => (p.id === post.id ? { ...p, ...data } : p)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const activeCategory = categories.find((c) => c.slug === slug);

  return (
    <>
      <Header onSearch={(q) => nav(q ? `/?search=${encodeURIComponent(q)}` : "/")} searchValue={search} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:py-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-3">
          <Sidebar categories={categories} />
        </div>

        <div className="lg:col-span-6 space-y-5">
          {/* Beta vendor cards temporarily hidden per request — backend + data intact. */}
          {false && <BetaProgramCards />}
          <div
            className="cp-card p-4 overflow-hidden"
            data-testid="widget-intro-video"
          >
            <div className="text-xs uppercase tracking-widest text-[#857163] mb-3 flex items-center gap-1.5">
              <span aria-hidden="true">🎥</span> Watch: Getting started
            </div>
            <div
              className="relative w-full rounded-2xl overflow-hidden border border-[#EBDCD1]"
              style={{ paddingBottom: "56.25%" }}
            >
              <iframe
                src="https://www.youtube.com/embed/WIX1Y5t40D4?rel=0"
                title="Cake Pop Talk intro video"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute top-0 left-0 w-full h-full"
                style={{ border: 0 }}
                data-testid="intro-video-iframe"
              />
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#857163] mb-1.5">
              {search ? "Search results" : activeCategory ? "Category" : "Feed"}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] leading-tight">
              {search ? (
                <>Results for <span className="text-[#FF7E67]">"{search}"</span></>
              ) : activeCategory ? (
                <>{activeCategory.name}</>
              ) : (
                <>The sweetest <span className="text-[#FF7E67]">baker</span> feed</>
              )}
            </h1>
            {activeCategory && (
              <p className="mt-2 text-[#857163]">{activeCategory.description}</p>
            )}
          </div>

          <div className="flex gap-2 items-center">
            <button
              onClick={() => setSort("recent")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition ${
                sort === "recent" ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
              }`}
              data-testid="sort-recent"
            >
              <Clock className="w-4 h-4" /> Recent
            </button>
            <button
              onClick={() => setSort("trending")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition ${
                sort === "trending" ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
              }`}
              data-testid="sort-trending"
            >
              <Flame className="w-4 h-4" /> Trending
            </button>
          </div>

          {loading ? (
            <div className="flex items-center gap-3 text-[#857163] py-8">
              <div className="cp-cakepop-loader" /> Loading fresh pops…
            </div>
          ) : posts.length === 0 ? (
            <div className="cp-card p-8 text-center">
              <div className="font-heading text-2xl text-[#3B2C24] mb-2">No posts yet</div>
              <p className="text-[#857163]">Be the first to share something sweet!</p>
            </div>
          ) : (
            posts.map((p) => <PostCard key={p.id} post={p} onLike={handleLike} />)
          )}
        </div>

        <div className="lg:col-span-3 space-y-5">
          {user === false && (
            <div
              className="cp-card p-5 relative overflow-hidden lg:sticky lg:top-24"
              data-testid="join-community-card"
              style={{ background: "linear-gradient(180deg, #FFFBF5 0%, #FFF5EA 100%)" }}
            >
              <div className="text-[11px] uppercase tracking-widest text-[#3B2C24] mb-3 flex items-center gap-1.5 font-semibold">
                <span aria-hidden="true">🧁</span> New here?
              </div>
              <h3 className="font-heading text-2xl leading-tight text-[#3B2C24] mb-3">
                Your Baking Business Starts Here
              </h3>
              <p className="text-sm text-[#5A4A40] mb-4">
                Launch your storefront, manage your customers, and grow your sales — all in one place.
              </p>
              <ul className="space-y-2 mb-5 text-sm text-[#3B2C24]">
                <li className="flex items-start gap-2">
                  <span aria-hidden="true" className="shrink-0">🍭</span>
                  <span>Open your own storefront and sell your creations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden="true" className="shrink-0">🍭</span>
                  <span>Promote your stand on the Find a Baker Map</span>
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden="true" className="shrink-0">🍭</span>
                  <span>Grow your business with built-in marketing and business tools</span>
                </li>
              </ul>
              <button
                onClick={() => nav("/register")}
                data-testid="join-community-cta"
                className="w-full py-3 rounded-2xl text-white font-semibold text-sm hover:opacity-90 transition inline-flex items-center justify-center gap-2 shadow-sm"
                style={{ background: "linear-gradient(135deg, #FF7E67, #FF9E85)" }}
              >
                Join Free Now <span aria-hidden="true">→</span>
              </button>
              <div className="mt-3 text-center text-[11px] text-[#857163]">
                Already a member?{" "}
                <button onClick={() => nav("/login")} className="underline hover:text-[#FF7E67]" data-testid="join-community-login-link">Log in</button>
              </div>
            </div>
          )}

          <div className="cp-card p-5" data-testid="widget-trending-tags">
            <div className="text-xs uppercase tracking-widest text-[#857163] mb-3">Trending tags</div>
            {tags.length === 0 ? (
              <div className="text-sm text-[#857163]">No tags yet — start posting!</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <button
                    key={t.tag}
                    onClick={() => nav(`/?search=${encodeURIComponent(t.tag)}`)}
                    className="cp-tag hover:bg-[#FFD07F]/50 transition"
                  >
                    #{t.tag} <span className="text-[#857163]">· {t.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
